import moment from 'moment';
import React from 'react';
import { ReminderContext, ReminderContextType, ServicesContext } from '~/services';
import { convertPagination, enumValue } from '~/util';

export function useReminderQuery() {
    const services = React.useContext(ServicesContext);
    const { filterValues, byMe, paginationRef, sort } = React.useContext(
        ReminderContext
    ) as ReminderContextType;

    const getQuery = React.useCallback(
        (isAggregate = false) => {
            const params: string[] = [];
            if (!isAggregate) {
                params.push(`page=${paginationRef.current.current}`);
                params.push(`items_per_page=${paginationRef.current.items_per_page}`);
                params.push(`order[${sort.field}]=${sort.dir}`);
            }
            if (!isAggregate && filterValues.viewStatus.includes('open')) {
                params.push(`task_status[]=${enumValue(['task', 'status', 'Not Started'])}`);
            }
            if (!isAggregate && filterValues.viewStatus.includes('inprogress')) {
                params.push(`task_status[]=${enumValue(['task', 'status', 'In Progress'])}`);
            }
            if (!isAggregate && filterValues.viewStatus.includes('done')) {
                params.push(`task_status[]=${enumValue(['task', 'status', 'Completed'])}`);
            }
            if (!isAggregate && filterValues.viewStatus.includes('na')) {
                params.push(`task_status[]=${enumValue(['task', 'status', 'Dismissed'])}`);
            }
            if (!isAggregate && filterValues.viewStatus.includes('missed')) {
                params.push(`task_status[]=${enumValue(['task', 'status', 'Missed'])}`);
            }
            if (!isAggregate && filterValues.hasOpenGap) {
                params.push('has_open_gap=1');
            }
            if (!isAggregate && filterValues.priority) {
                params.push(`notification_date_type=${filterValues.priority}`);
            }
            if (filterValues.dueDateStart)
                params.push(
                    `due_at[gte]=${moment(filterValues.dueDateStart).format(
                        'YYYY-MM-DD 00:00:00%2B00:00'
                    )}`
                );
            if (filterValues.dueDateEnd)
                params.push(
                    `due_at[lt]=${moment(filterValues.dueDateEnd)
                        .add('1', 'd')
                        .format('YYYY-MM-DD 00:00:00%2B00:00')}`
                );
            if (filterValues.createdByType)
                params.push(`created_by_type_filter=${filterValues.createdByType}`);
            if (filterValues.clinic)
                filterValues.clinic.forEach((x) => {
                    params.push(`clinics.id[]=${x.value}`);
                });
            if (filterValues.PCP)
                filterValues.PCP.forEach((x) => {
                    params.push(`pcps.id[]=${x.value}`);
                });
            if (filterValues.measureCode) {
                filterValues.measureCode.forEach((x) => {
                    params.push(`timely_action_measure_code[]=${x.value}`);
                });
            }
            if (byMe) params.push(`assigned_user.id=${services.user.get().id}`);
            return `?${params.join('&')}`;
        },
        // Since paginationRef is declared in the context, eslint doesn't know
        // it's actually a ref and can be excluded.
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [filterValues, byMe, services.user, sort]
    );

    const getReminders = React.useCallback(async () => {
        const result = await services.api.call({
            method: 'get',
            url: `/api/tasks${getQuery()}`,
            data: {},
            headers: {
                'Content-Type': 'application/ld+json',
            },
        });
        paginationRef.current = convertPagination(result.data);
        return result.data['hydra:member'];
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [getQuery, services.api]);

    const getReminderAggregates = React.useCallback(async () => {
        const result = await services.api.call({
            method: 'get',
            url: `/api/task-aggregates${getQuery(true)}`,
        });
        return result;
    }, [getQuery, services.api]);

    return { getReminders, getReminderAggregates };
}
